import {
  COMBINE_BROADCAST_R,
  COMBINE_COPY,
  COMBINE_EXTRACT_ALPHA,
  COMBINE_RGB_ALPHA_R,
  EDGE_REFLECT,
  FILTER_LANCZOS3,
  GpuContext,
  GpuImage,
  UNPACK_RGB,
  alphaFill,
  combine,
  guidedFilter,
  packTensor,
  resample,
  tensorByteLength,
  tensorDims,
  unpackTensor,
  unsharpMask,
  type TensorSpec,
  type TexFormat,
} from '@design-ai/gpu-core';
import { OnnxModel, TensorBufferCache, type ModelIO } from '@design-ai/inference';
import type { UpscaleSettings } from './settings.js';

export interface UpscalePlan {
  scale: number;
  outputWidth: number;
  outputHeight: number;
  /** Network input edge, including context padding on all sides. */
  tileSize: number;
  overlap: number;
  /** Pixels of source each tile actually contributes. */
  coreSize: number;
  tilesX: number;
  tilesY: number;
  totalTiles: number;
  outputFormat: TexFormat;
}

export interface UpscaleProgress {
  tile: number;
  totalTiles: number;
  stage: 'color' | 'alpha' | 'finish';
}

const MAX_HALF_FLOAT_OUTPUT_PIXELS = 40_000_000;

/**
 * Works out tiling and validates that the result can exist on this GPU.
 *
 * Failing here with a clear message beats failing three minutes into a job with
 * an out-of-memory device loss - a 2000px crop at 4x is a 64 megapixel target,
 * which is already past `maxTextureDimension2D` on plenty of hardware.
 */
export function planUpscale(
  ctx: GpuContext,
  sourceWidth: number,
  sourceHeight: number,
  io: ModelIO,
  settings: UpscaleSettings,
): UpscalePlan {
  const scale = Math.max(1, Math.round(io.outputScale));
  const outputWidth = sourceWidth * scale;
  const outputHeight = sourceHeight * scale;

  const limit = ctx.caps.maxTextureDimension2D;
  if (outputWidth > limit || outputHeight > limit) {
    throw new Error(
      `[pipeline] ${sourceWidth}x${sourceHeight} at ${scale}x needs ${outputWidth}x${outputHeight}, ` +
        `but this GPU caps textures at ${limit}px. Crop tighter, trim transparent margin first, or pick a lower-scale model.`,
    );
  }

  const multiple = Math.max(1, io.sizeMultiple ?? 1);
  const overlap = Math.max(0, Math.round(settings.overlap));
  // The network input has to satisfy the model's size multiple, and fp16 packing
  // needs an even width; rounding the whole tile up covers both.
  let tileSize = roundUp(Math.max(64, Math.round(settings.tileSize)), Math.max(2, multiple));
  if (typeof io.size === 'number') tileSize = io.size;

  const coreSize = tileSize - overlap * 2;
  if (coreSize <= 0) {
    throw new Error(`[pipeline] overlap ${overlap} leaves no usable area in a ${tileSize}px tile`);
  }

  const tilesX = Math.ceil(sourceWidth / coreSize);
  const tilesY = Math.ceil(sourceHeight / coreSize);

  // Half-float doubles the memory of an already large target; drop to 8-bit for
  // very big outputs, where the extra precision is invisible anyway.
  const outputFormat: TexFormat =
    outputWidth * outputHeight > MAX_HALF_FLOAT_OUTPUT_PIXELS ? 'rgba8unorm' : 'rgba16float';

  return {
    scale,
    outputWidth,
    outputHeight,
    tileSize,
    overlap,
    coreSize,
    tilesX,
    tilesY,
    totalTiles: tilesX * tilesY,
    outputFormat,
  };
}

/**
 * Tiled super-resolution with alpha handling.
 *
 * Tiles carry context padding into the network and only their centre is written
 * to the output, so tiles never overlap in the destination and no seam blending
 * is needed. `tensor_unpack.wgsl` writing an arbitrary sub-rectangle to an
 * arbitrary offset is what makes that a single dispatch per tile.
 */
export class UpscaleStage {
  readonly #buffers: TensorBufferCache;

  constructor(
    private readonly ctx: GpuContext,
    private readonly model: OnnxModel,
    private readonly io: ModelIO,
  ) {
    this.#buffers = new TensorBufferCache(ctx);
  }

  async run(
    source: GpuImage,
    settings: UpscaleSettings,
    onProgress?: (progress: UpscaleProgress) => void,
    signal?: AbortSignal,
  ): Promise<GpuImage> {
    const plan = planUpscale(this.ctx, source.width, source.height, this.io, settings);

    // Extend artwork colour under transparent pixels before inference, or the
    // network sharpens whatever garment texture happens to be under them and it
    // bleeds back across the edge when alpha is re-applied.
    const prepared = settings.inpaintTransparent
      ? this.ctx.image(source.width, source.height, 'rgba16float', 'sr-inpainted')
      : source;
    if (prepared !== source) alphaFill(this.ctx, source, prepared);

    const color = await this.#runTiled(prepared, plan, 'color', onProgress, signal);
    if (prepared !== source) prepared.release();

    const alpha = await this.#upscaleAlpha(source, color, plan, settings, onProgress, signal);

    onProgress?.({ tile: plan.totalTiles, totalTiles: plan.totalTiles, stage: 'finish' });

    const merged = this.ctx.image(plan.outputWidth, plan.outputHeight, plan.outputFormat, 'sr-merged');
    combine(this.ctx, color, alpha, merged, COMBINE_RGB_ALPHA_R);
    color.release();
    alpha.release();

    if (settings.sharpenAmount <= 0) {
      this.ctx.flush();
      return merged;
    }

    const sharpened = this.ctx.image(
      plan.outputWidth,
      plan.outputHeight,
      plan.outputFormat,
      'sr-sharpened',
    );
    unsharpMask(this.ctx, merged, sharpened, {
      amount: settings.sharpenAmount,
      radius: settings.sharpenRadius,
      threshold: settings.sharpenThreshold,
    });
    merged.release();
    await this.ctx.sync();
    return sharpened;
  }

  /** Runs every tile of `src` through the network into a fresh output texture. */
  async #runTiled(
    src: GpuImage,
    plan: UpscalePlan,
    stage: UpscaleProgress['stage'],
    onProgress?: (progress: UpscaleProgress) => void,
    signal?: AbortSignal,
  ): Promise<GpuImage> {
    const spec: TensorSpec = {
      width: plan.tileSize,
      height: plan.tileSize,
      channels: this.io.channels,
      dtype: this.io.dtype,
      layout: this.io.layout,
    };
    const inputBuffer = this.#buffers.get(tensorByteLength(spec), 'sr-input');
    const dst = this.ctx.image(plan.outputWidth, plan.outputHeight, plan.outputFormat, `sr-${stage}`);

    let index = 0;
    for (let ty = 0; ty < plan.tilesY; ty++) {
      for (let tx = 0; tx < plan.tilesX; tx++) {
        if (signal?.aborted) {
          dst.release();
          throw new DOMException('upscale aborted', 'AbortError');
        }

        const coreX = tx * plan.coreSize;
        const coreY = ty * plan.coreSize;

        packTensor(this.ctx, src, inputBuffer, spec, {
          rect: {
            x: coreX - plan.overlap,
            y: coreY - plan.overlap,
            width: plan.tileSize,
            height: plan.tileSize,
          },
          edge: EDGE_REFLECT,
          mean: this.io.mean,
          std: this.io.std,
        });
        await this.ctx.sync();

        const output = await this.model.runGpu(
          this.ctx,
          inputBuffer,
          tensorDims(spec),
          this.io.dtype,
        );

        const outputSpec: TensorSpec = {
          width: output.dims[output.dims.length - 1] ?? plan.tileSize * plan.scale,
          height: output.dims[output.dims.length - 2] ?? plan.tileSize * plan.scale,
          channels: this.io.outputChannels,
          dtype: output.dtype,
          layout: this.io.layout,
        };

        // Clamp the written region to the output so the last row/column of tiles
        // does not run past the edge.
        const copyWidth = Math.min(plan.coreSize, src.width - coreX) * plan.scale;
        const copyHeight = Math.min(plan.coreSize, src.height - coreY) * plan.scale;
        const denorm = this.io.outputDenorm;

        unpackTensor(this.ctx, output.buffer, dst, outputSpec, {
          mode: UNPACK_RGB,
          srcOrigin: [plan.overlap * plan.scale, plan.overlap * plan.scale],
          copySize: [copyWidth, copyHeight],
          dstOrigin: [coreX * plan.scale, coreY * plan.scale],
          ...(denorm ? { scale: denorm.scale, bias: denorm.bias } : {}),
        });

        // Submit before releasing ORT's output buffer: it goes straight back to
        // ORT's allocator and the next inference could overwrite it.
        await this.ctx.sync();
        output.dispose();

        index++;
        onProgress?.({ tile: index, totalTiles: plan.totalTiles, stage });
      }
    }
    return dst;
  }

  async #upscaleAlpha(
    source: GpuImage,
    upscaledColor: GpuImage,
    plan: UpscalePlan,
    settings: UpscaleSettings,
    onProgress?: (progress: UpscaleProgress) => void,
    signal?: AbortSignal,
  ): Promise<GpuImage> {
    const sourceAlpha = this.ctx.image(source.width, source.height, 'r32float', 'sr-alpha-src');
    combine(this.ctx, source, source, sourceAlpha, COMBINE_EXTRACT_ALPHA);

    if (settings.alphaMode === 'model') {
      // Broadcast the matte to RGB and push it through the same network. Most
      // faithful, and twice the cost.
      const asColor = this.ctx.image(source.width, source.height, 'rgba16float', 'sr-alpha-rgb');
      combine(this.ctx, source, sourceAlpha, asColor, COMBINE_BROADCAST_R);
      sourceAlpha.release();

      const upscaled = await this.#runTiled(asColor, plan, 'alpha', onProgress, signal);
      asColor.release();

      const out = this.ctx.image(plan.outputWidth, plan.outputHeight, 'r32float', 'sr-alpha-out');
      combine(this.ctx, upscaled, upscaled, out, COMBINE_COPY);
      upscaled.release();
      return out;
    }

    const upsampled = this.ctx.image(plan.outputWidth, plan.outputHeight, 'r32float', 'sr-alpha-up');
    resample(this.ctx, sourceAlpha, upsampled, { filter: FILTER_LANCZOS3 });
    sourceAlpha.release();

    if (settings.alphaMode === 'lanczos') {
      this.ctx.flush();
      return upsampled;
    }

    // `guided`: snap the interpolated matte onto the edges the SR model just
    // produced. Cheap, and it removes the softness Lanczos leaves behind.
    const guided = this.ctx.image(plan.outputWidth, plan.outputHeight, 'r32float', 'sr-alpha-guided');
    guidedFilter(this.ctx, upscaledColor, upsampled, guided, {
      radius: 4,
      eps: 1e-5,
      subsample: 2,
    });
    upsampled.release();
    this.ctx.flush();
    return guided;
  }

  dispose(): void {
    this.#buffers.destroy();
  }
}

function roundUp(value: number, multiple: number): number {
  return Math.ceil(value / multiple) * multiple;
}
